
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import ChatRepositoryInterface, {
  ChatInterface, chatRateMap, ChatsSearchResultInterface,
  ChatsWSMessageInterface,
} from '@/services/QA/chat/interfaces';
import { AxiosError } from 'axios';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import AuthRepositoryInterface from '@/services/Auth/Interfaces';
import DepartmentRepositoryInterface from '@/services/Departments/Interfaces';
import { RawLocation } from 'vue-router';

@Component({
  mixins: [
    Themeable,
  ],
  components: {},
  inject: [
    'chatRepository',
    'userRepository',
    'departmentRepository',
  ],
  sockets: {
    async chatsData(msg: ChatsWSMessageInterface): Promise<void> {
      this.$emit('chatsData', msg.chats, msg.totalCount);
    },
    searchResultData(msg: ChatsSearchResultInterface): void {
      this.$emit('searchResult', msg.chats, msg.totalItems);
    },
  },
})
export default class ChatsQueue extends Vue {
  private loading: boolean = true;
  private chats: ChatInterface[] = [];
  private search: string = '';
  private chatRate: { '0': string, '1': string, '2': string } = chatRateMap;
  private totalItems: number = 0;
  private currentPage: number = 1;
  private pageCache: number = 1;
  private itemsPerPage: number = 10;

  private supportAgents: UserBaseInfoInterface[] = [];
  private agentEmail: string | null = null;
  private agentReviewedIds: string[] = [];
  private reviewedIdsMap: { [page: number]: string[] } = {};

  private agentSearch: boolean = false;
  private leftStep: boolean = false;

  private messageIds: string[] = [];
  private chatControllerIds: string[] = [];

  private responseReceived: boolean = false;

  private headers: DataTableHeader[] = [
    { text: '#', value: 'num', sortable: false },
    { text: 'ID', value: 'id' },
    { text: 'External', value: 'external', sortable: false },
    { text: 'Date & Time', value: 'endedTimestamp' },
    { text: 'Chat Rated', value: 'rate', sortable: false },
    { text: 'Client Name', value: 'visitor.name', sortable: false },
    { text: 'Client Logged', value: 'visitor.isLogged', sortable: false },
    { text: 'Participants', value: 'agents', sortable: false },
  ];

  private sortBy: string = 'endedTimestamp';
  private sortDesc: boolean = true;
  private options: { sortBy: string[], sortDesc: boolean[] } = { sortBy: [this.sortBy], sortDesc: [this.sortDesc] };


  private readonly chatRepository!: ChatRepositoryInterface;
  private readonly userRepository!: UserRepoInterface;
  private readonly departmentRepository!: DepartmentRepositoryInterface;

  public beforeCreate(): void {
    this.$on('chatsData', (chats: ChatInterface[], totalCount: number) => {
      if (!this.leftStep && this.agentSearch && !this.reviewedIdsMap[this.currentPage]) {
        this.totalItems += totalCount;
      }
      this.handleChatsMessage(chats);
    });

    this.$on('searchResult', (chats: ChatInterface[], totalItems: number) => {
      this.totalItems = totalItems;
      this.chats = chats;
      this.loading = false;
      this.$forceUpdate();
    });
  }

  public async created(): Promise<void> {
    const agentEmail = this.$route.params.agentEmail;
    const page = this.$route.params.page;

    if (agentEmail && page) {
      this.agentEmail = agentEmail;
      this.currentPage = Number(page);
      this.pageCache = Number(page);
      this.totalItems = Number(this.$route.params.totalItems);
      this.reviewedIdsMap = JSON.parse(this.$route.params.reviewedIdsMap);
      this.agentReviewedIds = this.reviewedIdsMap[this.currentPage - 1] || [];
      this.sortBy = this.$route.params.sortBy;
      this.sortDesc = Boolean(Number(this.$route.params.sortDesc));
      this.options.sortBy = [this.sortBy];
      this.options.sortDesc = [this.sortDesc];
      await this.filterChatQueue(agentEmail);
    } else {
      await this.initialize();
    }
    await this.fetchSupportAgents();
  }

  private async initialize(): Promise<void> {
    this.agentSearch = false;
    this.responseReceived = false;
    const [, totalItems] = await this.chatRepository
      .getAllUnreviewedChats(this.currentPage, this.sortBy, this.sortDesc);
    this.totalItems = totalItems;
    this.responseReceived = true;
  }

  private async fetchSupportAgents(): Promise<void> {
    const departments = await this.departmentRepository.getAllDepartments();
    const supportDepartment = departments.find((department) => department.name === 'Support');
    if (!supportDepartment) {
      this.$errorDialog.show('\'Support\' Department not found...');
      return;
    }

    this.supportAgents = await this.userRepository
      .getUsersByDepartment(supportDepartment._id) as unknown as UserBaseInfoInterface[];
  }

  private async changeSelectedAgent(agentEmail: string): Promise<void> {
    this.agentReviewedIds = [];
    this.reviewedIdsMap = {};
    this.leftStep = false;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageCache = 1;
    await this.filterChatQueue(agentEmail);
  }

  private async filterChatQueue(agentEmail: string): Promise<void> {
    this.agentSearch = true;
    this.responseReceived = false;
    this.chats = [];
    this.loading = true;

    if (!agentEmail) {
      await this.initialize();
      return;
    }

    const [agentChatIds] = await this.chatRepository
      .getAgentChatIds(agentEmail, this.currentPage, this.sortBy, this.sortDesc, this.agentReviewedIds);
    this.responseReceived = true;
    this.chatControllerIds = agentChatIds;

    if (!agentChatIds.length) {
      this.chats = [];
      this.loading = false;
      return;
    }

  }

  private handleChatsMessage(chats: ChatInterface[]): void {
    if (!this.responseReceived) {
      setTimeout(() => {
        this.handleChatsMessage(chats);
      }, 200);

      return;
    }

    if (!this.loading) {
      return;
    }

    this.chats = chats;
    if (this.chatControllerIds.length > 0) {
      this.messageIds = chats.map((chat) => chat.id);

      const filteredIds = this.messageIds.filter((messageId) => !this.chatControllerIds.includes(messageId));

      if (!this.leftStep) {
        this.reviewedIdsMap[this.currentPage] = [
          ...(this.reviewedIdsMap[this.currentPage - 1] || []),
          ...filteredIds,
        ];
      }

      this.agentReviewedIds = [
        ...this.reviewedIdsMap[this.currentPage] || [],
      ];

    }

    this.loading = false;
  }

  private async chatsReview(): Promise<void> {
    await this.$router.push({ name: 'chat_review' });
  }

  private async changePage(): Promise<void> {
    this.leftStep = false;
    if (this.currentPage === this.pageCache) {
      return;
    }

    if (this.currentPage < this.pageCache) {
      this.leftStep = true;
      this.agentReviewedIds = this.reviewedIdsMap[this.currentPage - 1] || [];
    }
    this.pageCache = this.currentPage;

    this.loading = true;
    if (this.agentEmail) {
      await this.filterChatQueue(this.agentEmail);
    } else {
      this.responseReceived = false;
      await this.chatRepository.getAllUnreviewedChats(this.currentPage, this.sortBy, this.sortDesc);
      this.responseReceived = true;
    }

  }

  private getReviewRoute(chatId: string): RawLocation {
    return {
      name: 'chat_review',
      params: {
        chatId,
        agentEmail: String(this.agentEmail),
        page: String(this.currentPage),
        totalItems: String(this.totalItems),
        reviewedIdsMap: JSON.stringify(this.reviewedIdsMap),
        sortBy: this.sortBy,
        sortDesc: String(Number(this.sortDesc)),
      },
    };
  }

  private async syncChats(): Promise<void> {
    try {
      await this.chatRepository.syncChats();
      this.$successDialog.show('Chats synchronization started!');
    } catch (e) {
      this.$errorDialog.show(JSON.stringify(e));
    }
  }

  @Watch('options')
  private async watchOptions(): Promise<void> {
    if (this.loading) {
      return;
    }

    const sortBy = this.options.sortBy[0];
    const sortDesc = this.options.sortDesc[0];

    if (!sortBy && !sortDesc) {
      this.sortBy = 'endedTimestamp';
      this.sortDesc = true;
    } else {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    }

    this.loading = true;
    if (this.agentEmail) {
      await this.filterChatQueue(this.agentEmail);
    } else {
      await this.initialize();
    }
  }
}
